/**
 * Reading the object manifest: what the modules declare (objects, their
 * lists, records, queues, children, related lists, lifecycle and commands).
 *
 * The manifest is generated at build time from `concepts.yaml` + OpenAPI
 * (platform contract spec §5.3). The app provides it once at the root; mock
 * mode and stories provide the example manifest. Every lookup of an unknown
 * id throws, naming the ids it knows, so a wrong id fails where it is written
 * rather than as a blank page.
 */
import {
  createContext,
  createElement,
  useContext,
  type ReactNode,
} from "react";

import { isFeatureEnabled } from "../../config/features";
import { paths } from "../../routing/routes";
import type {
  CommandId,
  ManifestCommand,
  ManifestObject,
  ObjectId,
  ObjectManifest,
  ObjectRef,
} from "./contract";

/** Fill `{param}` in a declared path template. */
export function fillPath(
  template: string,
  values: Record<string, string>,
): string {
  return template.replace(/\{([^}]+)\}/g, (_, name: string) => {
    const value = values[name];
    if (value === undefined) {
      throw new Error(
        `Path "${template}" needs "${name}", which was not given (given: ${Object.keys(values).join(", ") || "none"}).`,
      );
    }
    return encodeURIComponent(value);
  });
}

export class UnknownObjectError extends Error {
  constructor(id: string, known: readonly string[]) {
    super(
      `Unknown object "${id}". The manifest declares: ${known.join(", ") || "(none)"}.`,
    );
    this.name = "UnknownObjectError";
  }
}

export class UnknownCommandError extends Error {
  constructor(id: string, known: readonly string[]) {
    super(
      `Unknown command "${id}". The manifest declares: ${known.join(", ") || "(none)"}.`,
    );
    this.name = "UnknownCommandError";
  }
}

/**
 * Whether an object is on for the person: every feature its routes need is on for them. An object whose
 * feature is off is built but switched off here (by the environment's flag, or a named-person trial the person
 * is not on): something someone can change, so One shows it with that reason rather than hiding it or showing
 * it broken (hide or block, Ben 16 Oct). Read at call time, because the feature map arrives with `/me` and
 * changes on sign-out.
 */
export function isObjectEnabled(object: ManifestObject): boolean {
  return object.features.every((feature) => isFeatureEnabled(feature));
}

/** Lookups over one manifest. Built once per manifest by `manifestIndex`. */
export interface ManifestIndex {
  manifest: ObjectManifest;
  /**
   * Declared by a module, on or off. Links and pages resolve any known object; an unknown id is not found.
   */
  isObjectKnown(id: ObjectId): boolean;
  /**
   * Known, and on for this person. Anything that reads the object's data (pickers, related lists, suggestions,
   * landing, search) asks this; a switched-off object has no records to offer.
   */
  isObjectEnabled(id: ObjectId): boolean;
  objectOf(id: ObjectId): ManifestObject;
  commandOf(id: CommandId): ManifestCommand;
  /** The object's collection commands (create …), in declaration order. */
  collectionCommandsOf(id: ObjectId): ManifestCommand[];
  /** Bulk commands acting on this object's records from its list. */
  bulkCommandsOf(id: ObjectId): ManifestCommand[];
  /**
   * The page a named record opens: the record page, or the list for a list-only object. Resolves for a
   * switched-off object too (to its "switched off" page); null only for an unknown or page-less object.
   */
  hrefOf(ref: ObjectRef): string | null;
}

const indexes = new WeakMap<ObjectManifest, ManifestIndex>();

export function manifestIndex(manifest: ObjectManifest): ManifestIndex {
  const cached = indexes.get(manifest);
  if (cached) return cached;
  const objects = new Map(manifest.objects.map((o) => [o.id, o]));
  const commands = new Map(manifest.commands.map((c) => [c.id, c]));
  const objectOf = (id: ObjectId): ManifestObject => {
    const found = objects.get(id);
    if (!found) throw new UnknownObjectError(id, [...objects.keys()]);
    return found;
  };
  const index: ManifestIndex = {
    manifest,
    isObjectKnown: (id) => objects.has(id),
    isObjectEnabled: (id) => {
      const object = objects.get(id);
      return !!object && isObjectEnabled(object);
    },
    objectOf,
    commandOf: (id) => {
      const found = commands.get(id);
      if (!found) throw new UnknownCommandError(id, [...commands.keys()]);
      return found;
    },
    collectionCommandsOf: (id) =>
      manifest.commands.filter(
        (c) => c.target === `collection:${id}` && !c.proposal_only,
      ),
    bulkCommandsOf: (id) =>
      manifest.commands.filter((c) => c.bulk && c.target === id),
    hrefOf: (ref) => {
      const object = objects.get(ref.object);
      if (!object) return null;
      if (object.record)
        return paths.objectRecord({ objectId: object.id, recordKey: ref.id });
      if (object.list) return paths.objectList({ objectId: object.id });
      return null;
    },
  };
  indexes.set(manifest, index);
  return index;
}

const ObjectManifestContext = createContext<ManifestIndex | null>(null);

export function ObjectManifestProvider({
  manifest,
  children,
}: {
  manifest: ObjectManifest;
  children: ReactNode;
}) {
  return createElement(
    ObjectManifestContext.Provider,
    { value: manifestIndex(manifest) },
    children,
  );
}

export function useObjectManifest(): ManifestIndex {
  const index = useContext(ObjectManifestContext);
  if (!index) {
    throw new Error(
      "useObjectManifest must be used within an ObjectManifestProvider",
    );
  }
  return index;
}

/** "Release lines…" → "Release lines": a command's label as a title. */
export function commandTitle(label: string): string {
  return label
    .replace(/…$/, "")
    .replace(/\.\.\.$/, "")
    .trim();
}
